import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SplitHeading from '../../components/ui/SplitHeading';
import BookCallModal from '../../components/BookCallModal';
import CheckForm from '../../features/siteCheck/CheckForm';
import LayaConsole from '../../features/siteCheck/LayaConsole';
import useGsap from '../../motion/useGsap';
import { prefersReducedMotion, isDesktop } from '../../motion/tokens';

const MeshGradient = lazy(() =>
  import('@paper-design/shaders-react').then((m) => ({ default: m.MeshGradient }))
);

const MOBILE_BREAKPOINT = 1024;
const STARTING_PHASES = new Set(['starting', 'running']);
const IDLE_PHASES = new Set(['idle', 'failed', 'done']);

// First viewport: the studio thesis in type on the left, the client hub
// you get on the right (clickable, fixture data). The atmosphere shader
// mounts after first paint so LCP stays the headline; on scroll the
// atmosphere dims into the manifesto.
export default function Hero({ siteCheck, industries = [] }) {
  const ref = useRef(null);
  const consoleRef = useRef(null);
  const previousPhase = useRef(siteCheck.phase);
  const [atmosphere, setAtmosphere] = useState(false);
  const [booking, setBooking] = useState(false);

  // The Bluprise console sits below the fold: when a
  // check starts, bring it into view once so the visitor sees it working
  // without having to scroll for it themselves.
  useEffect(() => {
    const wasIdle = IDLE_PHASES.has(previousPhase.current);
    const nowStarting = STARTING_PHASES.has(siteCheck.phase);
    if (wasIdle && nowStarting && window.innerWidth < MOBILE_BREAKPOINT && consoleRef.current) {
      consoleRef.current.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }
    previousPhase.current = siteCheck.phase;
  }, [siteCheck.phase]);

  // Mount the shader after first paint, desktop only, and unmount it as soon
  // as the hero leaves the viewport so it never burns GPU below the fold.
  useEffect(() => {
    if (prefersReducedMotion() || !isDesktop() || !ref.current) return undefined;
    let idle = null;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        idle = 'requestIdleCallback' in window ? window.requestIdleCallback(() => setAtmosphere(true), { timeout: 1500 }) : window.setTimeout(() => setAtmosphere(true), 500);
      } else {
        setAtmosphere(false);
      }
    }, { threshold: 0.05 });
    io.observe(ref.current);
    return () => {
      io.disconnect();
      if (idle && 'cancelIdleCallback' in window) window.cancelIdleCallback(idle);
    };
  }, []);

  useGsap(ref, (gsap) => {
    if (prefersReducedMotion() || !isDesktop()) return;
    const st = { trigger: ref.current, start: 'top top', end: 'bottom top', scrub: true };
    gsap.to('[data-hero-atmos]', { opacity: 0.1, ease: 'none', scrollTrigger: st });
    gsap.fromTo('[data-hero-sub]', { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 1, delay: 0.7, ease: 'expo.out', stagger: 0.12 });
  }, []);

  return (
    <section ref={ref} className="relative flex min-h-[92svh] flex-col justify-center overflow-hidden pb-12 pt-28 md:pb-16 lg:pt-32">
      <div data-hero-atmos className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_70%_20%,rgba(29,78,216,0.35),transparent_70%)]" />
        {atmosphere ? (
          <Suspense fallback={null}>
            <MeshGradient
              className="absolute inset-0 h-full w-full opacity-70"
              colors={['#060a12', '#0b1a3d', '#1d4ed8', '#0b1220', '#5ea0ff']}
              speed={0.12}
              backgroundColor="transparent"
            />
          </Suspense>
        ) : null}
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-ink to-transparent" />
      </div>

      <div className="container-x grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-16" data-hero-copy>
        <div>
          <p data-hero-sub className="mb-6 text-sm font-medium tracking-wide text-sky">Websites · SEO · AI · API Integrations · AGI Bots · Travel Tech</p>
          <SplitHeading lines={['Built to run.', 'Not to demo.']} className="display-lg max-w-[12ch]" />
          <p data-hero-sub className="lede mt-8">
            Bluprise builds websites, grows your search presence, connects your APIs, and creates AI tools,
            AGI bots and travel tech. Start with a website check to see what Google and your customers see.
          </p>
          <div data-hero-sub className="mt-8">
            <CheckForm
              busy={siteCheck.phase === 'starting' || siteCheck.phase === 'running'}
              error={siteCheck.phase === 'failed' ? siteCheck.error : ''}
              onSubmit={siteCheck.start}
            />
          </div>
          <p data-hero-sub className="mt-5 text-sm text-mist">
            Rather talk to a person?{' '}
            <button type="button" onClick={() => setBooking(true)} className="text-cream underline underline-offset-4 hover:text-sky">Book a free call</button>
            {' · '}
            <Link to="/contact" className="text-cream underline underline-offset-4 hover:text-sky">Start a project</Link>
          </p>
          <p data-hero-sub className="mt-6 text-sm text-dim">
            120+ products shipped · 11 years · BluBeds, Nighthouse, Postify, FillAI and ValidMy.email are ours
          </p>
        </div>
        <div ref={consoleRef} data-hero-sub className="w-full lg:max-w-[560px] lg:justify-self-end">
          <LayaConsole phase={siteCheck.phase} check={siteCheck.check} />
        </div>
      </div>
      <BookCallModal open={booking} onClose={() => setBooking(false)} />
    </section>
  );
}
